import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronUp } from 'lucide-react';

const PriceChart = ({ rollingData, currency, unit }) => {
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(400);
  const [selectedTimeframe, setSelectedTimeframe] = useState('Monthly');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const timeframes = ['Monthly', 'Yearly'];

  const normalizeToken = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim();
    if (!normalized) return '';
    if (['null', 'none', 'undefined'].includes(normalized.toLowerCase())) return '';
    return normalized;
  };

  const resolvedUnitLabel = useMemo(() => {
    const currencyPart = normalizeToken(currency);
    const unitPart = normalizeToken(unit);
    if (currencyPart && unitPart) return `${currencyPart}/${unitPart}`;
    if (currencyPart) return currencyPart;
    if (unitPart) return unitPart;
    return '';
  }, [currency, unit]);

  // Transform API data to chart format based on timeframe
  const transformApiData = (apiData, timeframe) => {
    if (!apiData) {
      return null;
    }

    let dataArray = [];
    let subtitle = '';
    let labelFormatter = null;

    // Select the appropriate data array based on timeframe
    switch (timeframe) {
      case 'Daily':
        if (!apiData.days || apiData.days.length === 0) return null;
        dataArray = apiData.days.filter(item => item.avg !== null); // Filter out null values
        subtitle = 'Daily average';
        labelFormatter = (item) => {
          const date = new Date(item.date);
          return `${date.getMonth() + 1}/${date.getDate()}`; // Short format: "9/24"
        };
        break;

      case 'Weekly':
        if (!apiData.weeks || apiData.weeks.length === 0) return null;
        dataArray = apiData.weeks.filter(item => item.avg !== null);
        subtitle = 'Weekly average';
        labelFormatter = (item) => {
          // Extract week number from "2025-W34" format
          const weekNum = item.week.split('-W')[1];
          return `W${weekNum}`;
        };
        break;

      case 'Monthly':
        if (!apiData.months || apiData.months.length === 0) return null;
        dataArray = apiData.months.filter(item => item.avg !== null);
        subtitle = 'Monthly average';
        labelFormatter = (item) => {
          const [, month] = item.month.split('-');
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return monthNames[parseInt(month) - 1];
        };
        break;

      case 'Yearly':
        if (!apiData.years || apiData.years.length === 0) return null;
        dataArray = apiData.years.filter(item => item.avg !== null);
        subtitle = 'Yearly average';
        labelFormatter = (item) => item.year.toString();
        break;

      default:
        return null;
    }

    if (dataArray.length === 0) return null;

    // Calculate overall average
    const totalAvg = dataArray.reduce((sum, item) => sum + item.avg, 0) / dataArray.length;

    // Transform to points
    const points = dataArray.map((item) => ({
      label: labelFormatter(item),
      value: item.avg,
      rawData: item
    }));

    return {
      value: totalAvg,
      unit: resolvedUnitLabel,
      subtitle: subtitle,
      points: points
    };
  };

  // Dummy data for different timeframes (fallback)
  const dummyChartData = {
    Daily: {
      value: 77650.00,
      unit: resolvedUnitLabel,
      subtitle: 'Daily average',
      points: [
        { label: 'D1', value: 150 },
        { label: 'D2', value: 280 },
        { label: 'D3', value: 320 },
        { label: 'D4', value: 180 },
        { label: 'D5', value: 380 },
        { label: 'D6', value: 340 },
        { label: 'D7', value: 360 }
      ]
    },
    Weekly: {
      value: 77650.00,
      unit: resolvedUnitLabel,
      subtitle: 'Weekly average',
      points: [
        { label: 'W7', value: 150 },
        { label: 'W8', value: 280 },
        { label: 'W9', value: 320 },
        { label: 'W10', value: 200 },
        { label: 'W11', value: 380 },
        { label: 'W12', value: 340 },
        { label: 'W13', value: 360 }
      ]
    },
    Monthly: {
      value: 82340.00,
      unit: resolvedUnitLabel,
      subtitle: 'Monthly average',
      points: [
        { label: 'M1', value: 180 },
        { label: 'M2', value: 250 },
        { label: 'M3', value: 310 },
        { label: 'M4', value: 220 },
        { label: 'M5', value: 350 },
        { label: 'M6', value: 380 },
        { label: 'M7', value: 320 }
      ]
    },
    Yearly: {
      value: 89720.00,
      unit: resolvedUnitLabel,
      subtitle: 'Yearly average',
      points: [
        { label: '2018', value: 200 },
        { label: '2019', value: 260 },
        { label: '2020', value: 180 },
        { label: '2021', value: 350 },
        { label: '2022', value: 380 },
        { label: '2023', value: 320 },
        { label: '2024', value: 390 }
      ]
    }
  };

  // Use API data if available, otherwise use dummy data
  const apiChartData = useMemo(() => transformApiData(rollingData, selectedTimeframe), [rollingData, selectedTimeframe]);
  const currentData = apiChartData || dummyChartData[selectedTimeframe];
  
  const safePoints = currentData?.points ?? [];
  const hasData = safePoints.length > 0;

  // Chart dimensions and responsive padding
  const chartHeight = 240;
  const paddingLeft = 50;
  const paddingRight = 50;
  const paddingTop = 12;
  const paddingBottom = 26;
  const plotWidth = Math.max(1, chartWidth - paddingLeft - paddingRight);
  const plotHeight = Math.max(1, chartHeight - paddingTop - paddingBottom);

  // Scale values - calculate from actual data with fallbacks
  const maxValue = hasData ? Math.max(...safePoints.map(p => p.value)) : 1;
  const minValue = hasData ? Math.min(...safePoints.map(p => p.value)) : 0;
  const valueRange = Math.max(0.0001, maxValue - minValue);

  // Add 10% padding to top and bottom of chart for better visualization
  const paddingPercent = 0.1;
  const yAxisMin = minValue - (valueRange * paddingPercent);
  const yAxisMax = maxValue + (valueRange * paddingPercent);
  const yAxisRange = yAxisMax - yAxisMin;

  // Generate dynamic grid values (4 evenly spaced lines)
  const generateGridValues = () => {
    const step = yAxisRange / 4;
    return [
      Math.round(yAxisMin + step),
      Math.round(yAxisMin + step * 2),
      Math.round(yAxisMin + step * 3),
      Math.round(yAxisMax)
    ];
  };

  const gridValues = generateGridValues();

  // Calculate positions
  const getX = (index) => {
    if (safePoints.length <= 1) return paddingLeft + plotWidth / 2;
    return paddingLeft + (index * plotWidth) / (safePoints.length - 1);
  };
  const getY = (value) => {
    // Scale based on actual data range with padding
    const normalizedValue = yAxisRange ? (value - yAxisMin) / yAxisRange : 0;
    return chartHeight - paddingBottom - (normalizedValue * plotHeight);
  };

  // Handle point hover
  const handlePointHover = (point, index) => {
    const x = getX(index);
    const y = getY(point.value);
    setHoveredPoint({ point, index });
    setTooltipPosition({ x, y });
  };

  const handlePointLeave = () => {
    setHoveredPoint(null);
  };

  // Custom Tooltip Component
  const CustomTooltip = ({ point, position }) => {
    if (!point) return null;

    return (
      <div style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y - 60}px`,
        transform: 'translateX(-50%)',
        background: 'rgba(30, 35, 50, 0.98)',
        border: '1px solid rgba(75, 85, 99, 0.3)',
        borderRadius: '8px',
        padding: '8px 12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
        color: 'white',
        fontSize: '13px',
        minWidth: '120px',
        zIndex: 1000,
        pointerEvents: 'none'
      }}>
        <div style={{ marginBottom: '4px' }}>
          <span style={{ color: '#9CA3AF', fontSize: '12px', fontWeight: '400' }}>
            {point.label}
          </span>
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ color: '#E5E7EB', fontWeight: '500', fontSize: '14px' }}>
            {point.value.toLocaleString()}{currentData.unit ? ` ${currentData.unit}` : ''}
          </div>
        </div>
      </div>
    );
  };

  // Measure container width for responsive layout
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const w = containerRef.current.getBoundingClientRect().width;
        // Account for container padding; keep a minimum inner width
        const inner = Math.max(220, w - 32);
        setChartWidth(Math.max(240, Math.floor(inner)));
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  if (!hasData) return null;

  return (
    <div style={{ width: '100%', maxWidth: '100%', margin: '0 auto' }} ref={containerRef}>
      <div
        style={{
          position: 'relative',
          borderRadius: '1.5rem',
          padding: '1.25rem',
          color: 'white',
          background: 'rgba(51, 51, 51, 0.2)',
          backdropFilter: 'blur(10px)',
          boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)'
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          marginBottom: '1.5rem' 
        }}>
          <div>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '0.25rem',
              margin: 0
            }}>
              {currentData.value.toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })}{currentData.unit ? ` ${currentData.unit}` : ''}
            </h2>
            <p style={{ 
              color: '#D1D5DB', 
              fontSize: '0.875rem',
              margin: 0
            }}>
              {currentData.subtitle}
            </p>
          </div>
          
          {/* Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'rgba(55, 65, 81, 0.5)',
                borderRadius: '0.5rem',
                padding: '0.5rem 0.75rem',
                fontSize: '0.875rem',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(55, 65, 81, 0.7)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(55, 65, 81, 0.5)'}
            >
              {selectedTimeframe}
              <ChevronUp 
                style={{
                  width: '1rem',
                  height: '1rem',
                  transition: 'transform 0.2s',
                  transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              />
            </button>
            
            {isDropdownOpen && (
              <div style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                marginTop: '0.5rem',
                backgroundColor: '#374151',
                borderRadius: '0.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #4B5563',
                zIndex: 10,
                minWidth: '100%'
              }}>
                {timeframes.map((timeframe, index) => (
                  <button
                    key={timeframe}
                    onClick={() => {
                      setSelectedTimeframe(timeframe);
                      setIsDropdownOpen(false);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.5rem 1rem',
                      fontSize: '0.875rem',
                      backgroundColor: timeframe === selectedTimeframe ? '#4B5563' : 'transparent',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      borderRadius: index === 0 ? '0.5rem 0.5rem 0 0' : 
                                   index === timeframes.length - 1 ? '0 0 0.5rem 0.5rem' : '0'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#4B5563'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = timeframe === selectedTimeframe ? '#4B5563' : 'transparent'}
                  >
                    {timeframe}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Chart */}
        <div style={{ position: 'relative' }}>
          <svg
            width="100%"
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            preserveAspectRatio="xMinYMin slice"
            style={{ overflow: 'visible' }}
          >
            {/* Grid lines (horizontal dotted) */}
            {gridValues.map((value) => (
              <line
                key={value}
                x1={paddingLeft}
                x2={chartWidth - paddingRight}
                y1={getY(value)}
                y2={getY(value)}
                stroke="rgba(255, 255, 255, 0.2)"
                strokeDasharray="2,4"
                strokeWidth="1"
              />
            ))}

            {/* Vertical grid lines (orange dotted) */}
            {currentData.points.map((point, index) => (
              <line
                key={`grid-vertical-${index}`}
                x1={getX(index)}
                x2={getX(index)}
                y1={paddingTop}
                y2={chartHeight - paddingBottom}
                stroke="rgba(255, 165, 0, 0.3)"
                strokeDasharray="2,4"
                strokeWidth="1"
              />
            ))}

            {/* X-axis line */}
            <line
              x1={paddingLeft}
              x2={chartWidth - paddingRight}
              y1={chartHeight - paddingBottom}
              y2={chartHeight - paddingBottom}
              stroke="rgba(255, 255, 255, 0.4)"
              strokeWidth="1"
            />

            {/* Vertical dotted lines from data points to x-axis */}
            {currentData.points.map((point, index) => (
              <line
                key={`data-vertical-${index}`}
                x1={getX(index)}
                x2={getX(index)}
                y1={getY(point.value)}
                y2={chartHeight - paddingBottom}
                stroke="rgba(255, 255, 255, 0.5)"
                strokeDasharray="2,4"
                strokeWidth="1"
              />
            ))}

            {/* Data points */}
            {currentData.points.map((point, index) => (
              <g key={index}>
                {/* Highlight circle when hovered - render first so it's behind */}
                {hoveredPoint?.index === index && (
                  <circle
                    cx={getX(index)}
                    cy={getY(point.value)}
                    r="6"
                    fill="white"
                    stroke="#8B5CF6"
                    strokeWidth="2"
                    style={{ pointerEvents: 'none' }}
                  />
                )}
                <circle
                  cx={getX(index)}
                  cy={getY(point.value)}
                  r="4"
                  fill="white"
                  stroke="none"
                  style={{ pointerEvents: 'none' }}
                />
                {/* Invisible larger circle for better hover area */}
                <circle
                  cx={getX(index)}
                  cy={getY(point.value)}
                  r="12"
                  fill="transparent"
                  stroke="none"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => handlePointHover(point, index)}
                  onMouseLeave={handlePointLeave}
                />
              </g>
            ))}
          </svg>
          
          {/* Y-axis labels - positioned to the left of grid lines */}
          {gridValues.map((value) => (
            <span
              key={value}
              style={{
                position: 'absolute',
                left: '0px',
                top: `${getY(value) - 8}px`,
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: '500',
                width: `${paddingLeft - 10}px`,
                textAlign: 'right'
              }}
            >
              {value.toLocaleString()}
            </span>
          ))}

          {/* X-axis labels - positioned below data points */}
          {currentData.points.map((point, index) => (
            <span
              key={index}
              style={{
                position: 'absolute',
                left: `${getX(index)}px`,
                top: `${chartHeight - paddingBottom + 10}px`,
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: '500',
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap'
              }}
            >
              {point.label}
            </span>
          ))}

          {/* Tooltip */}
          {hoveredPoint && (
            <CustomTooltip
              point={hoveredPoint.point}
              position={tooltipPosition}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceChart;
